import { useState, useEffect } from 'react';
import { Button } from 'antd-mobile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    Store,
    UserCheck,
    AlertCircle,
    Loader2,
    Clock
} from 'lucide-react';
import liff from '@line/liff';
import { useStore } from '../store';
import { api } from '../api';
import './JoinPage.css';

export default function JoinPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dbUser = useStore((s) => s.dbUser);
    const setDbUser = useStore((s) => s.setDbUser);
    const showToast = useStore((s) => s.showToast);
    const ready = useStore((s) => s.isReady);

    const [loading, setLoading] = useState(false);
    const [autoJoining, setAutoJoining] = useState(false);
    const [success, setSuccess] = useState(false);
    const [storeName, setStoreName] = useState('');
    const [ownerName, setOwnerName] = useState('');
    const [error, setError] = useState('');

    // Get token from URL params
    const token = searchParams.get('token');

    // Auto-join when token is present AND LIFF is ready
    useEffect(() => {
        if (token && ready && !success && !error && !autoJoining) {
            handleAutoJoin();
        }
    }, [token, ready, dbUser]);

    const handleAutoJoin = async () => {
        if (!token) return;

        // Check if already staff
        if (dbUser?.role === 'STAFF') {
            setError('คุณเป็น Staff ของร้านอื่นอยู่แล้ว');
            return;
        }

        setAutoJoining(true);
        setLoading(true);
        setError('');

        try {
            // Make sure LIFF is initialized and user is logged in
            if (!liff.isLoggedIn()) {
                // Redirect to login, will come back to this page after
                liff.login({ redirectUri: window.location.href });
                return;
            }

            // Get LINE ID Token from LIFF
            const lineIdToken = liff.getIDToken();

            if (!lineIdToken) {
                // Try to get access token as fallback info
                console.error('ID Token is null, access token:', liff.getAccessToken());
                setError('ไม่สามารถยืนยันตัวตนได้ กรุณาปิดและเปิดลิงก์ใหม่');
                return;
            }

            // Call API to join store
            const res = await api.joinStore(token, lineIdToken);

            setStoreName(res.shop_name || 'ร้านค้า');
            setOwnerName(res.owner_name || 'เจ้าของร้าน');
            setSuccess(true);

            // Refresh user data
            if (dbUser) {
                const { data: updatedUser } = await api.getUser(dbUser.line_user_id);
                if (updatedUser) {
                    setDbUser(updatedUser);
                }
            }

            // Redirect to OA after 2 seconds if backend suggests it
            if (res.redirect_to_oa && res.oa_url) {
                const oaUrl = res.oa_url;
                setTimeout(() => {
                    window.location.href = oaUrl;
                }, 2500);
            }
        } catch (err: any) {
            console.error('Join error:', err);
            setError(err.message || 'ลิงก์ไม่ถูกต้องหรือหมดอายุแล้ว');
        } finally {
            setLoading(false);
            setAutoJoining(false);
        }
    };

    // Already a staff
    if (dbUser?.role === 'STAFF' && !token) {
        return (
            <div className="page join-page">
                <div className="join-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <ArrowLeft size={24} />
                    </button>
                    <h1>เข้าร่วมร้าน</h1>
                </div>

                <div className="join-content">
                    <div className="already-staff">
                        <Store size={48} color="#4facfe" />
                        <h2>คุณเป็น Staff อยู่แล้ว</h2>
                        <p>หากต้องการเข้าร่วมร้านอื่น กรุณาออกจากร้านปัจจุบันก่อน</p>
                        <Button
                            color="primary"
                            onClick={() => navigate('/profile')}
                        >
                            ไปหน้าโปรไฟล์
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="page join-page">
                <div className="join-content success-content">
                    <div className="success-icon">
                        <UserCheck size={64} color="#00C853" />
                    </div>
                    <h2>เข้าร่วมร้านสำเร็จ!</h2>
                    <p>คุณได้เข้าร่วมร้าน "{storeName}" แล้ว</p>
                    <p className="sub-text">
                        โดย {ownerName}
                    </p>
                    <p className="sub-text" style={{ marginTop: 8 }}>
                        กำลังพาคุณไปหน้าแชท...
                    </p>
                    <div style={{ marginTop: 16, fontSize: 12, color: '#888' }}>
                        หากไม่ถูกพาไปอัตโนมัติ กดปุ่มด้านล่าง
                    </div>
                    <Button
                        block
                        color="primary"
                        size="large"
                        onClick={() => { window.location.href = 'https://line.me/R/ti/p/@643oswjj'; }}
                        style={{ marginTop: 16 }}
                    >
                        ไปหน้าแชท
                    </Button>
                </div>
            </div>
        );
    }

    // Loading state when auto-joining
    if (loading && token) {
        return (
            <div className="page join-page">
                <div className="join-content success-content">
                    <div className="loading-icon">
                        <Loader2 size={64} color="#4facfe" className="spin" />
                    </div>
                    <h2>กำลังเข้าร่วมร้าน...</h2>
                    <p>กรุณารอสักครู่</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error && token) {
        const isExpired = error.includes('หมดอายุ') || error.includes('expired');

        return (
            <div className="page join-page">
                <div className="join-header">
                    <button className="back-btn" onClick={() => navigate('/')}>
                        <ArrowLeft size={24} />
                    </button>
                    <h1>เข้าร่วมร้าน</h1>
                </div>

                <div className="join-content">
                    <div className="error-state">
                        {isExpired ? (
                            <Clock size={64} color="#ff9800" />
                        ) : (
                            <AlertCircle size={64} color="#ff5252" />
                        )}
                        <h2>{isExpired ? 'ลิงก์หมดอายุแล้ว' : 'ไม่สามารถเข้าร่วมได้'}</h2>
                        <p>{error}</p>
                        {isExpired && (
                            <p className="sub-text" style={{ marginTop: 8, color: '#666' }}>
                                กรุณาขอลิงก์ใหม่จากเจ้าของร้าน
                            </p>
                        )}
                        <Button
                            color="primary"
                            onClick={() => navigate('/')}
                            style={{ marginTop: 24 }}
                        >
                            กลับหน้าหลัก
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // No token - show info page
    return (
        <div className="page join-page">
            <div className="join-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={24} />
                </button>
                <h1>เข้าร่วมร้าน</h1>
            </div>

            <div className="join-content">
                <div className="join-icon">
                    <Store size={48} color="#4facfe" />
                </div>

                <h2>รอรับคำเชิญจากเจ้าของร้าน</h2>
                <p>เจ้าของร้านจะส่งลิงก์เชิญมาให้คุณผ่าน LINE Chat</p>

                <div className="join-info">
                    <h4>วิธีเข้าร่วมร้าน</h4>
                    <ul>
                        <li>ขอให้เจ้าของร้านส่งคำเชิญผ่าน LINE</li>
                        <li>กดลิงก์ที่ได้รับเพื่อเข้าร่วมร้าน</li>
                        <li>เมื่อเข้าร่วมแล้วจะสามารถบันทึกยอดขายได้</li>
                    </ul>
                </div>

                <Button
                    block
                    onClick={() => navigate('/')}
                    style={{ marginTop: 24 }}
                >
                    กลับหน้าหลัก
                </Button>
            </div>
        </div>
    );
}
